import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as ApproveButton } from '../../../assets/icons/action-approve.svg';
import { ReactComponent as RejectButton } from '../../../assets/icons/action-reject.svg';
import {
  ElectricityPrice,
  ElectricityPriceRequest,
} from '../../../interface/electricity-price.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import { setSelectedStation } from '../../../redux/slices/StationManagementSidePanelSlice';
import {
  fetchAllUnapprovedElectricityPrice,
  fetchUnApprivedElectricPendingCount,
} from '../../../redux/slices/UnapprovedElectricityPriceSlice';
import CustomRadioButton from '../../../shared-components/custom-radio-button/CustomRadioButton';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import SuccessNotification from '../../../shared-components/success-notification/SuccessNotification';
import StationSidePanel from '../../../shared-components/table-side-panel/StationSidePanel';
import electricityPrice from '../../../utils/api/electricity-price';

import RejectElectricPriceReason from './RejectElectricPriceReason';


/**
 * Component to list and change status of the electricity price for station
 * @param param0
 * @returns
 */
export default function UnapprovedElectricityPrice({
  currentPage,
  setCurrentPage,
}: {
  currentPage: string;
  setCurrentPage: (value: string) => void;
}) {
  const { t } = useTranslation('station');
  const dispatch = useAppDispatch();
  const {
    electricityPricing,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    // filterListLoading,
    // filters,
    // isFilterUpdated,
  } = useAppSelector((s) => s.unapprovedElectricityPrice);

  const [openStatus, setOpenStatus] = useState<boolean>(false);
  const [modalView, setModalView] = useState({
    title: '',
    header: false,
    content: <div />,
    notification: false,
  });
  const navigate = useNavigate();

  /**
   * Function to open device view model
   */
  const handleOpenModelView = (chargingStation: ElectricityPrice, newTab: boolean) => {
    if (chargingStation) {
      dispatch(
        setSelectedStation({
          id: chargingStation?._id,
          name: chargingStation?.chargingStationName,
        }),
      );
      if (newTab) {
        const url = `/ev-admin/charging-station/unapproved/station/${chargingStation._id}/station-dashboard?searchBy=${chargingStation.chargingStationName}`;
        window.open(url, '_blank'); // opens in a new tab
      } else {
        navigate({
          pathname: `/ev-admin/charging-station/unapproved/station/${chargingStation._id}/station-dashboard`,
          search: `?searchBy=${chargingStation.chargingStationName}`,
        });
      }
    } else {
      console.log('Error while opening device in separate page');
    }
  };

  /**
   * Handle the fetch the data in api function
   * @param data
   */
  const fetchData = (data: ElectricityPriceRequest) => {
    if (!loading) {
      dispatch(fetchAllUnapprovedElectricityPrice(data));
    }
  };

  /**
   * Function to update status of update price by ID
   * @param id
   * @param status
   */
  const handleChangeStatusOfUpdatedPricing = async (
    id: string,
    status: 'approved' | 'rejected',
    reason?: string,
  ) => {
    try {
      await electricityPrice.updateStatusElectricityPriceHistory(id, { status, reason });
      setModalView({
        title: '',
        header: false,
        content: (
          <SuccessNotification
            title={`Electricity price ${status} successfully!`}
            description=""
            onNotificationClose={() => setOpenStatus(false)}
          />
        ),
        notification: true,
      });
      setOpenStatus(true);
      fetchData({
        page,
        limit,
      });
      dispatch(fetchUnApprivedElectricPendingCount());
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          message: error?.response?.data?.message || 'Something went wrong!',
          status: true,
          type: 'error',
          code: error?.response?.data?.code,
        }),
      );
    }
  };

  /**
   * Component to open modal-box and get reason for price rejection
   * @param id
   */
  const getReasonForRejection = (id: string) => {
    setOpenStatus(true);
    setModalView({
      title: '',
      header: false,
      content: (
        <RejectElectricPriceReason
          id={id}
          handleCancel={() => setOpenStatus(false)}
          handleRejected={handleChangeStatusOfUpdatedPricing}
        />
      ),
      notification: false,
    });
  };

  /**
   * Function to render activity buttons
   * @param rowData
   * @returns
   */
  const renderActivityButton = (rowData: ElectricityPrice) => {
    return (
      <div className="action-button-group">
        <div
          className="action-button-item view-color"
          role="presentation"
          onClick={() => handleChangeStatusOfUpdatedPricing(rowData.id, 'approved')}>
          <ApproveButton />
        </div>
        <div
          className="action-button-item delete-color"
          role="presentation"
          onClick={() => getReasonForRejection(rowData.id)}>
          <RejectButton />
        </div>
      </div>
    );
  };

  const column = [
    {
      label: t('date'),
      accessorKey: 'createdAt' as keyof ElectricityPrice,
      sortAllow: false,
    },
    {
      label: t('stationName'),
      accessorKey: 'chargingStationName' as keyof ElectricityPrice,
      sortAllow: false,
      queryParamsKey: 'stationId',
      queryParamsValue: 'chargingStation',
      sidePanelComponent: <StationSidePanel fetchIdFrom="queryParams" />,
      handleClickExpandView: handleOpenModelView,
    },
    {
      label: t('currentPrice'),
      accessorKey: 'existingElectricityCostByUtilityProvider' as keyof ElectricityPrice,
      sortAllow: false,
      /**
       * Function to display currentPrice
       * @param rowData
       * @returns
       */
      customData: (rowData: ElectricityPrice) => (
        <div className="electricity-cost">
          {rowData?.existingElectricityCostByUtilityProvider}/-
        </div>
      ),
    },
    {
      label: t('updatedPrice'),
      accessorKey: 'electricityCostByUtilityProvider' as keyof ElectricityPrice,
      sortAllow: false,
      /**
       * Function to display electricityCostByUtilityProvider
       * @param rowData
       * @returns
       */
      customData: (rowData: ElectricityPrice) => (
        <div className="electricity-cost">{rowData?.electricityCostByUtilityProvider}/-</div>
      ),
    },
    {
      columnWidth: 50,
      label: t('actions'),
      accessorKey: 'actions' as keyof ElectricityPrice,
      sortAllow: false,
      /**
       * Function to display action buttons
       * @param rowData
       * @returns
       */
      customData: (rowData: ElectricityPrice) => renderActivityButton(rowData),
    },
  ];

  /**
   * Table pagination function
   * @param value
   */
  const pageNateLimitChange = (value: ElectricityPriceRequest) => {
    // const queryParameters = GetParametersFromCommonFilters(filters);

    fetchData({ ...value });
  };

  /**
   * handleAvailabilityChange
   */
  const handleAvailabilityChange = (name: string, value: string) => {
    setCurrentPage(value);
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    fetchData({ page, limit });
  };

  useEffect(() => {
    if (electricityPricing.length === 0) {
      fetchData({
        page,
        limit,
      });
    }
  }, []);

  return (
    <div className="table-content">
      <ModalBox
        open={openStatus}
        title={modalView.title}
        content={modalView.content}
        closeModal={() => setOpenStatus(false)}
        header={modalView.header}
        rightCorner=""
        notification={modalView.notification}
      />
      <CustomTable
        dataKey="ElectricityPricePending"
        header={column}
        data={electricityPricing}
        serialNo
        limit={limit}
        loading={loading}
        version="v2"
        filters={[]}
        filterListToDispatch={{} as any}
        ReferenceCurrentPages={handleRefresh}
        height={window.innerHeight >= 700 ? 327 : 329}
        page={page}
        totalPages={totalPages}
        totalResults={totalResults}
        pageLimitChange={pageNateLimitChange}
        sizeChangeButtonRequired
        rightTopContent={
          <div className="revenue-management-toggle-wrap">
            <CustomRadioButton
              name=""
              value="station"
              label={t('station')}
              selected={currentPage === 'station'}
              onChangeFunc={handleAvailabilityChange}
            />
            <CustomRadioButton
              name=""
              value="electricityPrice"
              label={t('electricityPrice')}
              selected={currentPage === 'electricityPrice'}
              onChangeFunc={handleAvailabilityChange}
            />
          </div>
        }
        refreshRequired
        handleRefresh={handleRefresh}
        hoverLabel="Refresh"
      />
    </div>
  );
}
